import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from '@stencil/core';

import { KEYBOARD } from '../../../config/constants';
import { DialogSizePreset } from '../../../services/interfaces';
import { blockScroll, hasSlotContent, isNumber } from '../../../services/utils';

@Component({
  tag: 'pk-dialog',
  styleUrl: './scss/pk-dialog.scss',
  shadow: true,
})
export class PkDialog {
  // Own Properties
  // ====================

  private timeout: number = 250;
  private dialogBaseElem: HTMLDivElement;
  private overlayElem: HTMLDivElement;
  private headerElem: HTMLDivElement;
  private footerElem: HTMLDivElement;
  private pkSimpleHeader: HTMLPkSimpleHeaderElement;
  private prevFocusedElem: HTMLElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkDialogElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() hasHeader: boolean = false;
  @State() footerSlotEmpty: boolean = false;

  // Public Property API
  // ========================

  /** If provided, it will set a custom height value for the dialog
   * ⚠️ Please notice that it will override the predefined size height value!
   */
  @Prop({ reflect: true }) height: number | string;

  /** If true, the close icon in the top right of the dialog won't be shown */
  @Prop({ reflect: true }) hideCloseButton: boolean = false;

  /** Whether if the modal dialog should be open (visible) or not */
  @Prop({ mutable: true, reflect: true }) isOpen: boolean = false;

  /**
   * Size of the modal.
   * This can be provided in three different ways:
   * - Using a predefined size option (`'small' | 'medium' | 'large' | 'full'`).
   * - Passing a `number` value that will set the width in pixels.
   * - Passing a `string` value tha can include pixels, percentage, will be directly applied as a style.
   */
  @Prop({ reflect: true }) size: DialogSizePreset = 'small';

  /** If provided, it will set a custom width value for the dialog
   * ⚠️ Please notice that it will override the predefined size width value!
   */
  @Prop({ reflect: true }) width: number | string;

  /** If true, clicking the modal overlay won't close it */
  @Prop({ reflect: true }) disableOverlayClose: boolean = false;

  /** If true, the page behind the modal will be scrollable */
  @Prop({ reflect: true }) allowWindowScroll: boolean = false;

  // Prop lifecycle events
  // =======================

  @Watch('isOpen')
  handleIsOpen() {
    this.isOpen ? this.onDialogOpen() : this.onDialogClose();
  }

  @Watch('isOpen')
  handleWindowScroll() {
    if (this.allowWindowScroll) return;

    blockScroll(this.isOpen, 'dialog');
  }

  @Watch('hideCloseButton')
  handleHideCloseButton() {
    if (!this.pkSimpleHeader) return;

    setTimeout(() => {
      this.pkSimpleHeader.disableClose = this.hideCloseButton;
    });
  }

  @Watch('height')
  setDialogHeight() {
    if (!this.dialogBaseElem) return;

    const DIALOG_HEIGHT_TOKEN = '--dialog--height';
    if (!this.height) {
      this.dialogBaseElem.style.removeProperty(DIALOG_HEIGHT_TOKEN);
      return;
    }

    this.dialogBaseElem.style.setProperty(
      DIALOG_HEIGHT_TOKEN,
      isNumber(+this.height) ? `${Number(this.height)}px` : this.height.toString(),
    );
  }

  @Watch('width')
  setDialogWidth() {
    if (!this.dialogBaseElem) return;

    const DIALOG_WIDTH_TOKEN = '--dialog--width';
    if (!this.width) {
      this.dialogBaseElem.style.removeProperty(DIALOG_WIDTH_TOKEN);
      return;
    }

    this.dialogBaseElem.style.setProperty(
      DIALOG_WIDTH_TOKEN,
      isNumber(+this.width) ? `${Number(this.width)}px` : this.width.toString(),
    );
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the dialog window will close */
  @Event({ cancelable: true, bubbles: false }) pkClose: EventEmitter;

  /** Callback handler to be called when the dialog window will open */
  @Event({ cancelable: true, bubbles: false }) pkOpen: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentDidLoad() {
    this.getDialogSimpleHeader();
    this.handleFooterSlotChange();
    this.handleHideCloseButton();
    this.handleIsOpen();
    this.handleWindowScroll();
    this.setDialogHeight();
    this.setDialogWidth();

    this.overlayElem.addEventListener('click', this.handleOverlayClick);
  }

  disconnectedCallback() {
    this.pkSimpleHeader?.removeEventListener('pkClose', this.closeDialog);
    this.overlayElem?.removeEventListener('click', this.handleOverlayClick);
  }

  // Listeners
  // ==============

  @Listen('keyup', { target: 'document' })
  handleDocumentKeyDown(event: KeyboardEvent) {
    if (event.key !== KEYBOARD.Escape || !this.isOpen) return;

    this.closeDialog();
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  /**
   * When called, it will open the dialog window.
   * @return {*}  {Promise<void>}
   */
  @Method()
  async open(): Promise<void> {
    this.openDialog();
  }

  /**
   * When called, it will close the dialog window.
   * @return {*}  {Promise<void>}
   */
  @Method()
  async close(): Promise<void> {
    this.closeDialog();
  }

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private getDialogSimpleHeader = () => {
    if (!this.headerElem) return;

    const pkSimpleHeader = this.headerElem.querySelector('slot').assignedElements({ flatten: true });
    if (!pkSimpleHeader.length || pkSimpleHeader[0].tagName.toLowerCase() !== 'pk-simple-header') {
      this.hasHeader = false;
      return;
    }

    this.hasHeader = true;
    this.pkSimpleHeader = pkSimpleHeader[0] as HTMLPkSimpleHeaderElement;
    this.pkSimpleHeader.addEventListener('pkClose', this.closeDialog);
  };

  private handleFooterSlotChange = () => {
    if (!this.footerElem) return;

    this.footerSlotEmpty = !hasSlotContent(this.footerElem, 'footer');
  };

  private openDialog = () => {
    this.isOpen = true;
  };

  private closeDialog = () => {
    this.isOpen = false;
  };

  private onDialogOpen = () => {
    const ev = this.pkOpen.emit();
    if (!ev.defaultPrevented) {
      // Get previous focused element in the page
      this.prevFocusedElem = document.activeElement as HTMLElement;
      this.handleFocus();
    }
  };

  private onDialogClose = () => {
    const ev = this.pkClose.emit();
    if (!ev.defaultPrevented) {
      // Return the focus to the previous element
      // that was active before the dialog open
      this.prevFocusedElem?.focus();
    }
  };

  private handleOverlayClick = () => {
    if (this.disableOverlayClose) return;

    this.closeDialog();
  };

  private handleFocus = () => {
    setTimeout(() => {
      this.dialogBaseElem.focus();
    }, this.timeout);
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <Host class={{ 'pk-dialog': true, 'is-open': this.isOpen }}>
        <div
          class="fixed left-0 top-0 h-full w-full bg-[color:--dialog--background-overlay]"
          part="overlay"
          ref={(overlay: HTMLDivElement) => (this.overlayElem = overlay)}
        />
        <div
          class={{
            'pk-dialog__base': true,
            [`size--${this.size}`]: !!this.size && DialogSizePreset.includes(`${this.size}`),
            'custom-height': !!this.height,
            'custom-width': !!this.width,
          }}
          part="base"
          ref={(dialog: HTMLDivElement) => (this.dialogBaseElem = dialog)}
          role="dialog"
          tabIndex={0}
        >
          <div
            class="relative flex rounded-t-[--dialog--border-radius]"
            part="header"
            ref={(header: HTMLDivElement) => (this.headerElem = header)}
          >
            {!this.hideCloseButton && !this.hasHeader && (
              <pk-button
                onPkClick={this.closeDialog}
                ariaLabel="close"
                appearance="icon"
                class="btn--close absolute right-0 top-0 pr-m pt-m"
                part="btn-close"
                role="button"
                type="button"
                soft
              >
                <span class="sr-only">Close</span>
                <pk-icon name="navigation-close" color="ui-04" role="img" title="close" />
              </pk-button>
            )}
            <slot name="header" onSlotchange={this.getDialogSimpleHeader} />
          </div>
          <div class="box-border flex flex-1 overflow-y-auto overflow-x-hidden" part="body">
            <slot name="body" />
          </div>
          <div
            ref={(footer: HTMLDivElement) => (this.footerElem = footer)}
            class={{
              'box-border flex min-h-[96px] rounded-b-[--dialog--border-radius] py-m': true,
              hidden: this.footerSlotEmpty,
            }}
            part="footer"
          >
            <slot name="footer" onSlotchange={this.handleFooterSlotChange} />
          </div>
        </div>
      </Host>
    );
  }
}
